import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DownloadSimple } from "@phosphor-icons/react";
import { BrutalButton } from "./Brutal";

/**
 * v403.7 PWA — "Install ReUP" button.
 * Renders ONLY when Chrome has fired `beforeinstallprompt` (captured early in
 * index.js as window.__reupInstallPrompt) and the app is not already running
 * standalone. Clicking triggers the native Android install prompt directly.
 */
export function InstallPWAButton({ className = "" }) {
  const { t } = useTranslation();
  const [ready, setReady] = useState(() => !!window.__reupInstallPrompt);

  const standalone =
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true;

  useEffect(() => {
    const onAvailable = () => setReady(true);
    const onInstalled = () => {
      window.__reupInstallPrompt = null;
      setReady(false);
      toast.success(t("pwa.installed_toast"));
    };
    window.addEventListener("reup-install-available", onAvailable);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("reup-install-available", onAvailable);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [t]);

  if (standalone || !ready) return null;

  const install = async () => {
    const promptEvent = window.__reupInstallPrompt;
    if (!promptEvent) return;
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    if (outcome === "accepted") {
      window.__reupInstallPrompt = null;
      setReady(false);
    }
  };

  return (
    <BrutalButton
      size="sm"
      variant="secondary"
      onClick={install}
      data-testid="install-pwa-btn"
      className={className}
      title={t("pwa.install_btn")}
    >
      <DownloadSimple size={14} weight="bold" />
      <span>{t("pwa.install_btn")}</span>
    </BrutalButton>
  );
}
